/**
 * Sheet para criar/editar um passo de follow-up — Mensagem e Destino.
 */
import React from 'react';
import { CheckIcon, ChevronRightIcon } from '../../../../components/icons';
import { Button } from '../../../../components/ui/Button';
import { FieldGroup } from '../../../../components/ui/field';
import { SidebarHeader } from '../../../kanban/components/SidebarHeader';
import { cn } from '@/lib/utils';
import type { FollowUpTemplateDraft } from '../hooks/useFollowUpTemplates';
import type { FollowUpSheetPayload } from '../types';
import { useFollowUpFormSheet } from '../hooks/useFollowUpFormSheet';
import FollowUpDestinationFields from './FollowUpDestinationFields';
import FollowUpMessageEditor from './FollowUpMessageEditor';
import FollowUpSheetTabs from './FollowUpSheetTabs';

interface FollowUpFormSheetProps {
    isOpen: boolean;
    onClose: () => void;
    onSave: (payload: FollowUpSheetPayload, options?: { silent?: boolean }) => void | Promise<void>;
    template?: FollowUpTemplateDraft | null;
}

export default function FollowUpFormSheet({
    isOpen,
    onClose,
    onSave,
    template,
}: FollowUpFormSheetProps) {
    const form = useFollowUpFormSheet({ isOpen, template, onSave, onClose });

    if (!isOpen) return null;

    return (
        <>
            <button
                type="button"
                className={cn('fixed inset-0 z-overlay overlay-premium', 'transition-base', 'cursor-default')}
                onClick={onClose}
                aria-label="Fechar formulário de follow-up"
            />

            <aside
                className={cn(
                    'fixed top-0 right-0 z-modal panel-sidebar flex h-full w-full flex-col',
                    'transition-base',
                    'sm:max-w-[31.25rem]',
                )}
                role="dialog"
                aria-modal="true"
                aria-labelledby="followup-sheet-title"
            >
                <SidebarHeader
                    title={form.isEditing ? 'Editar follow-up' : 'Novo follow-up'}
                    subtitle={
                        form.isEditing
                            ? 'Atualize a mensagem, o destino e o disparo.'
                            : 'Defina a mensagem, para quem enviar e quando disparar.'
                    }
                    onClose={onClose}
                    titleId="followup-sheet-title"
                />

                <form onSubmit={form.handleSubmit} className="flex min-h-0 flex-1 flex-col overflow-hidden bg-surface-0" noValidate>
                    <div className="border-b border-border px-s4 py-s3 md:px-s6">
                        <FollowUpSheetTabs value={form.tab} onChange={form.setTab} />
                    </div>

                    <FieldGroup className="scrollbar-premium min-h-0 flex-1 overflow-y-auto px-s4 py-s5 md:px-s6 md:py-s6">
                        {form.tab === 'mensagem' && (
                            <FollowUpMessageEditor
                                fieldId={form.fieldId}
                                value={form.message}
                                onChange={form.setMessage}
                                mediaUrl={form.mediaUrl}
                                onMediaChange={form.setMediaUrl}
                            />
                        )}

                        {form.tab === 'destino' && (
                            <FollowUpDestinationFields
                                fieldId={form.fieldId}
                                delayValue={form.delayValue}
                                delayUnit={form.delayUnit}
                                onDelayValueChange={form.setDelayValue}
                                onDelayUnitChange={form.setDelayUnit}
                                stageIds={form.settings.stageIds}
                                onAddStage={form.settings.addStageId}
                                onRemoveStage={form.settings.removeStageId}
                                timezone={form.settings.timezone}
                                onTimezoneChange={form.settings.setTimezone}
                                businessHoursStart={form.settings.businessHoursStart}
                                onBusinessHoursStartChange={form.settings.setBusinessHoursStart}
                                businessHoursEnd={form.settings.businessHoursEnd}
                                onBusinessHoursEndChange={form.settings.setBusinessHoursEnd}
                                businessHoursWeekdaysOnly={form.settings.businessHoursWeekdaysOnly}
                                onBusinessHoursWeekdaysOnlyChange={form.settings.setBusinessHoursWeekdaysOnly}
                            />
                        )}
                    </FieldGroup>

                    <footer className="shrink-0 border-t border-border bg-surface-subtle px-s4 py-s4 md:px-s6">
                        <Button
                            type="submit"
                            size="lg"
                            className="min-h-11 w-full gap-s2 touch-manipulation"
                            disabled={
                                form.isMessageStep
                                    ? false
                                    : form.isSaving || form.settings.saving || form.settings.loading
                            }
                        >
                            {form.isMessageStep ? (
                                <>
                                    Próximo
                                    <ChevronRightIcon className="size-5 shrink-0" aria-hidden />
                                </>
                            ) : (
                                <>
                                    <CheckIcon className="size-5 shrink-0" aria-hidden />
                                    {form.isSaving || form.settings.saving
                                        ? 'Salvando...'
                                        : form.isEditing
                                          ? 'Salvar alterações'
                                          : 'Salvar follow-up'}
                                </>
                            )}
                        </Button>
                    </footer>
                </form>
            </aside>
        </>
    );
}
